import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, API } from '../lib/api';
import { downloadFile } from '../lib/download';
import { Download, Percent, ArrowLeft, FileText } from 'lucide-react';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const currentFy = () => {
  const d = new Date();
  const y = d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}-${String(y + 1).slice(-2)}`;
};

const fyOptions = () => {
  const y0 = new Date().getFullYear();
  return [y0 + 1, y0, y0 - 1, y0 - 2, y0 - 3].map((y) => `${y}-${String(y + 1).slice(-2)}`);
};

const Kpi = ({ label, value, tone, testId }) => (
  <div className="card p-4" data-testid={testId}>
    <div className="uppercase tracking-wide text-[10px] font-semibold" style={{ color: 'var(--cc-text-muted)' }}>{label}</div>
    <div className="font-mono-data font-bold text-lg sm:text-xl break-words" style={{ color: tone || 'var(--cc-dark-green)' }}>{value}</div>
  </div>
);

export default function GstSummaryPage() {
  const [fy, setFy] = useState(currentFy());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/reports/gst-summary', { params: { fy } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => console.error('GST summary failed', e))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [fy]);

  const t = data?.totals;
  const activeMonths = useMemo(() => (data?.months || []).filter((m) => m.invoice_count > 0), [data]);

  const handleExport = async () => {
    setExporting(true);
    try { await downloadFile(`${API}/reports/gst-summary/excel?fy=${fy}`, `GST_Summary_FY${fy}.xlsx`); }
    finally { setExporting(false); }
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="gst-summary-page">
      <Link to="/monthly-statement" className="inline-flex items-center gap-1 text-sm mb-4" style={{ color: 'var(--cc-text-muted)' }} data-testid="gst-back-link">
        <ArrowLeft size={14} /> Monthly Statement
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-head text-3xl font-extrabold flex items-center gap-2" style={{ color: 'var(--cc-dark-green)' }}>
            <Percent size={26} /> GST Summary
          </h1>
          <p className="text-sm" style={{ color: 'var(--cc-text-muted)' }}>
            Month-wise CGST / SGST vs IGST on Tax Invoices for GSTR filing. Proforma invoices are excluded.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2 [&_.btn]:w-full sm:[&_.btn]:w-auto">
          <select className="input sm:w-40" value={fy} onChange={(e) => setFy(e.target.value)} data-testid="gst-fy-select">
            {fyOptions().map((f) => <option key={f} value={f}>FY {f}</option>)}
          </select>
          <button onClick={handleExport} disabled={exporting || !data} className="btn btn-outline" data-testid="btn-export-gst-excel">
            <Download size={16} /> {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
      </div>

      {t && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <Kpi label="Taxable Value" value={inr(t.taxable)} testId="gst-kpi-taxable" />
          <Kpi label="CGST" value={inr(t.cgst)} tone="#1D4ED8" testId="gst-kpi-cgst" />
          <Kpi label="SGST" value={inr(t.sgst)} tone="#1D4ED8" testId="gst-kpi-sgst" />
          <Kpi label="IGST" value={inr(t.igst)} tone="#B45309" testId="gst-kpi-igst" />
          <Kpi label="Total GST" value={inr(t.total_tax)} tone="#065F46" testId="gst-kpi-total" />
        </div>
      )}

      <div className="card overflow-hidden mb-6" data-testid="gst-monthly-card">
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--cc-border)' }}>
          <h2 className="font-head font-bold text-base md:text-lg" style={{ color: 'var(--cc-dark-green)' }}>Month-wise GST — FY {fy}</h2>
          {t && <span className="text-xs" style={{ color: 'var(--cc-text-muted)' }}>{t.invoice_count} tax invoices · {t.intra_count} intra-state · {t.inter_count} inter-state</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="cc-table" data-testid="gst-monthly-table">
            <thead>
              <tr>
                <th className="text-left">Month</th>
                <th className="text-right">Invoices</th>
                <th className="text-right">Taxable Value</th>
                <th className="text-right">CGST</th>
                <th className="text-right">SGST</th>
                <th className="text-right">IGST</th>
                <th className="text-right">Total Tax</th>
                <th className="text-right">Invoice Total</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-6 text-sm" style={{ color: 'var(--cc-text-muted)' }}>Loading…</td></tr>}
              {!loading && (data?.months || []).map((m) => (
                <tr key={m.key} data-testid={`gst-month-row-${m.key}`} style={m.invoice_count === 0 ? { opacity: 0.45 } : undefined}>
                  <td className="font-medium">{m.label}</td>
                  <td className="text-right font-mono-data">{m.invoice_count}</td>
                  <td className="text-right font-mono-data">{inr(m.taxable)}</td>
                  <td className="text-right font-mono-data">{inr(m.cgst)}</td>
                  <td className="text-right font-mono-data">{inr(m.sgst)}</td>
                  <td className="text-right font-mono-data" style={m.igst > 0 ? { color: '#B45309', fontWeight: 600 } : undefined}>{inr(m.igst)}</td>
                  <td className="text-right font-mono-data font-semibold">{inr(m.total_tax)}</td>
                  <td className="text-right font-mono-data">{inr(m.invoice_total)}</td>
                </tr>
              ))}
              {!loading && t && (
                <tr className="font-bold" style={{ background: 'var(--cc-surface)' }} data-testid="gst-total-row">
                  <td>Total</td>
                  <td className="text-right font-mono-data">{t.invoice_count}</td>
                  <td className="text-right font-mono-data">{inr(t.taxable)}</td>
                  <td className="text-right font-mono-data">{inr(t.cgst)}</td>
                  <td className="text-right font-mono-data">{inr(t.sgst)}</td>
                  <td className="text-right font-mono-data">{inr(t.igst)}</td>
                  <td className="text-right font-mono-data">{inr(t.total_tax)}</td>
                  <td className="text-right font-mono-data">{inr(t.invoice_total)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card overflow-hidden" data-testid="gst-invoices-card">
        <button type="button" onClick={() => setShowInvoices((v) => !v)} className="w-full px-4 py-3 border-b flex items-center justify-between text-left" style={{ borderColor: 'var(--cc-border)' }} data-testid="gst-toggle-invoices">
          <span className="font-head font-bold text-base md:text-lg flex items-center gap-2" style={{ color: 'var(--cc-dark-green)' }}><FileText size={16} /> Invoice-wise breakup ({data?.invoices?.length || 0})</span>
          <span className="text-xs" style={{ color: 'var(--cc-text-muted)' }}>{showInvoices ? 'Hide' : 'Show'}</span>
        </button>
        {showInvoices && (
          <div className="overflow-x-auto">
            <table className="cc-table" data-testid="gst-invoices-table">
              <thead>
                <tr>
                  <th className="text-left">Date</th>
                  <th className="text-left">Invoice No</th>
                  <th className="text-left">Client</th>
                  <th className="text-left">GSTIN / POS</th>
                  <th className="text-left">Type</th>
                  <th className="text-right">Taxable</th>
                  <th className="text-right">CGST</th>
                  <th className="text-right">SGST</th>
                  <th className="text-right">IGST</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data?.invoices || []).map((r) => (
                  <tr key={r.id} data-testid={`gst-invoice-row-${r.id}`}>
                    <td className="font-mono-data whitespace-nowrap">{r.invoice_date}</td>
                    <td className="font-mono-data">{r.invoice_no}</td>
                    <td>{r.client_name}</td>
                    <td className="text-xs" style={{ color: 'var(--cc-text-muted)' }}>{r.client_gstin || '—'}<br />{r.place_of_supply || '—'}</td>
                    <td>
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border" style={r.gst_type === 'igst' ? { background: '#FFFBEB', color: '#92400E', borderColor: '#FCD34D' } : { background: '#EFF6FF', color: '#1D4ED8', borderColor: '#93C5FD' }}>
                        {r.gst_type === 'igst' ? 'IGST' : 'CGST+SGST'}
                      </span>
                    </td>
                    <td className="text-right font-mono-data">{inr(r.taxable)}</td>
                    <td className="text-right font-mono-data">{inr(r.cgst)}</td>
                    <td className="text-right font-mono-data">{inr(r.sgst)}</td>
                    <td className="text-right font-mono-data">{inr(r.igst)}</td>
                    <td className="text-right font-mono-data font-semibold">{inr(r.invoice_total)}</td>
                  </tr>
                ))}
                {!data?.invoices?.length && <tr><td colSpan={10} className="text-center py-6 text-sm" style={{ color: 'var(--cc-text-muted)' }}>No tax invoices in this financial year.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {activeMonths.length === 0 && !loading && data && (
        <p className="text-xs mt-3" style={{ color: 'var(--cc-text-muted)' }} data-testid="gst-empty-hint">No tax invoices dated within FY {fy}. Pick another year above.</p>
      )}
    </div>
  );
}
